import { useEffect, useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { api } from '../lib/api';
import { useAuthStore } from '../lib/store';
import { Instagram, Facebook, Linkedin } from 'lucide-react';

export default function Settings() {
  const { token } = useAuthStore();
  const [accounts, setAccounts] = useState<any[]>([]);
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      api.setToken(token);
      loadSettings();
    }
  }, [token]);

  const loadSettings = async () => {
    try {
      const [accountsData, profileData] = await Promise.all([
        api.getSocialAccounts(),
        api.getBusinessProfile(),
      ]);
      setAccounts(accountsData);
      setProfile(profileData);
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const connectPlatform = async (platform: string) => {
    try {
      const { auth_url } = await api.connectSocial(platform);
      window.location.href = auth_url;
    } catch (error) {
      console.error('Failed to connect platform:', error);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600 mt-2">Manage your account and connected platforms</p>
        </div>

        {/* Connected Accounts */}
        <div className="card">
          <h2 className="text-xl font-semibold mb-4">Connected Accounts</h2>
          <div className="grid gap-4">
            <PlatformCard
              icon={<Instagram className="w-6 h-6" />}
              name="Instagram"
              connected={accounts.some((a) => a.platform === 'instagram')}
              onConnect={() => connectPlatform('instagram')}
            />
            <PlatformCard
              icon={<Facebook className="w-6 h-6" />}
              name="Facebook"
              connected={accounts.some((a) => a.platform === 'facebook')}
              onConnect={() => connectPlatform('facebook')}
            />
            <PlatformCard
              icon={<Linkedin className="w-6 h-6" />}
              name="LinkedIn"
              connected={accounts.some((a) => a.platform === 'linkedin')}
              onConnect={() => connectPlatform('linkedin')}
            />
          </div>
        </div>

        {/* Business Profile */}
        <div className="card">
          <h2 className="text-xl font-semibold mb-4">Business Profile</h2>
          <div className="space-y-4">
            <div>
              <label className="label">Business Name</label>
              <input
                type="text"
                className="input"
                defaultValue={profile?.business_name || ''}
                placeholder="Your business name"
              />
            </div>
            <div>
              <label className="label">Industry</label>
              <input
                type="text"
                className="input"
                defaultValue={profile?.industry || ''}
                placeholder="e.g., Food & Beverage"
              />
            </div>
            <div>
              <label className="label">Brand Voice</label>
              <input
                type="text"
                className="input"
                defaultValue={profile?.brand_voice || ''}
                placeholder="e.g., Professional, Casual, Friendly"
              />
            </div>
            <button className="btn btn-primary">Save Changes</button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function PlatformCard({
  icon,
  name,
  connected,
  onConnect,
}: {
  icon: React.ReactNode;
  name: string;
  connected: boolean;
  onConnect: () => void;
}) {
  return (
    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
      <div className="flex items-center gap-3">
        <div className="text-gray-700">{icon}</div>
        <div>
          <p className="font-medium text-gray-900">{name}</p>
          <p className="text-sm text-gray-500">
            {connected ? 'Connected' : 'Not connected'}
          </p>
        </div>
      </div>
      <button
        onClick={onConnect}
        className={`btn ${connected ? 'btn-secondary' : 'btn-primary'}`}
        disabled={connected}
      >
        {connected ? 'Connected' : 'Connect'}
      </button>
    </div>
  );
}
